import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { AuthService } from "../../../../services/auth.service";
import { MessageModel } from "../../../../models/message.model";
import { MessageService } from "../../../../services/message.service";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from 'rxjs';
import { DialogService } from "../../../../services/dialog.service";

@Component({
  selector: "app-header",
  standalone: false,
  templateUrl: "./header.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./header.component.scss"]
})
export class HeaderComponent implements OnInit, OnChanges {
  @Input() message: MessageModel | null = null;
  @Input() heading: string | null | undefined = null;
  @Input() numberOfVersions: number = 1;
  displayHeading = '';
  @Input() laterMessageExists: boolean = false;
  @Input() showApp = false;
  @Input() isNewMessage = false;
  @Output() changeVersion: EventEmitter<number> = new EventEmitter();
  @Output() saveChanges: EventEmitter<string> = new EventEmitter();
  @Output() closeMessage: EventEmitter<string> = new EventEmitter();
  @Output() changeMessageType = new EventEmitter<boolean>();
  showButton: boolean = true;
  checkboxSelected: boolean = false;
  private messageTypeSubscription: Subscription = new Subscription();
  currentMessageType: string = '';

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private activatedRoute: ActivatedRoute,
    private dialogService: DialogService
  ) {
    this.messageService.getCheckboxState().subscribe(state => {
      this.checkboxSelected = state;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['heading'] || changes['message']) {
      this.displayHeading = this.message?.id ? (this.heading ?? '') : '';
    }
  }

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe(param => {
      this.showButton = !param.has("id");
    });
    // Ensure user groups are loaded
    this.authService.userGroupsReady$.subscribe(loaded => {
      if (!loaded && this.authService.getActiveAccount()) {
        this.authService.getGraphUser();
      }
    });
    this.messageTypeSubscription = this.messageService.currentMessageType.subscribe(type => {
      this.currentMessageType = type;
    });
  }

  ngOnDestroy() {
    this.messageTypeSubscription.unsubscribe();
  }

  isReadOnly() {
    return !this.authService.hasReadWriteGroups();
  }

  onVersionChange(nextVersion: boolean) {
    if (nextVersion) {
      let nextVersionNumber = this.message!.ver + 1;
      if (nextVersionNumber <= this.numberOfVersions) {
        this.changeVersion.emit(nextVersionNumber);
      }
    } else {
      let previousVersionNumber = this.message!.ver - 1;
      if (previousVersionNumber > 0) {
        this.changeVersion.emit(previousVersionNumber);
      }
    }
  }

  onSave() {
    this.saveChanges.emit(this.message?.id!);
  }

  onClose() {
    if (!this.messageService.hasChanges()) {
      this.closeMessage.emit(this.message!.id!);
    } else {
      this.dialogService.unsavedChangesConfirmation().subscribe(result => {
        if (result) {
          this.closeMessage.emit(this.message!.id!);
        }
      });
    }
  }

  canClose() {
    return true;
  }

  canEdit() {
    return this.message!.ver >= this.numberOfVersions;
  }

  onKlart() {
    this.messageService.applyKlart.emit();
  }

  onUseAsTemplate() {
    this.messageService.useAsTemplate.emit(this.message!.id!);
  }

  canUpdate(): boolean {

    return this.message!.ver >= this.numberOfVersions && this.messageService.hasChanges();
  }

  isFormValid(): boolean {
    if (this.messageService.errors.size > 0) {
      return false;
    }
    
    if (this.currentMessageType === 'TOT') {
      if (this.message?.timeIntervalEnabled) {
        const hasStart = !!(this.message.timeIntervalStart && this.message.timeIntervalStart !== "");
        const hasEnd = !!(this.message.timeIntervalEnd && this.message.timeIntervalEnd !== "");
        
        if (!hasStart || !hasEnd) {
          return false;
        }
      }
      
      return true;
    }
    
    return this.checkboxSelected;
  }

  openNewWindow() {
    let id = this.message?.id;
    if (id) {
      this.messageService.clickOpenInNewWindow.emit(id);
    } else {
      id = this.messageService.getNewMessageID();
      this.messageService.clickOpenInNewWindow.emit("new_message");
    }
    window.open(
      "/trafikinfoPopup/" + id,
      "_blank",
      this.messageService.getNewMessageWindowSpecs()
    );
  }

  onChangeMessageType(shouldShowApp: boolean) {
    this.changeMessageType.emit(shouldShowApp);
  }

  onMessageTypeTabChange(index: number) {
    if (!this.isNewMessage) {
      return;
    }

    const shouldShowApp = index === 0;
    if (shouldShowApp !== this.showApp) {
      this.onChangeMessageType(shouldShowApp);
    }
  }

}
